import React, { useCallback } from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import { getPickListStatusLabel } from '../../../../common/utils/pickList/getPickListStatusLabel';
import Text from '../../../components/text/Text';
import { MachineChip, StatusBadge, Tag } from '../../../design/components';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { isRowIncompletePick, isRowLabelOnly } from '../../myDay/usePickerMyDay';

const ORDER_SIZE = 28;

const getStatus = (row, isCompleted) => {
	if (!isCompleted && isRowLabelOnly(row)) {
		return { label: 'Label only', tone: 'primary' };
	}
	if (row.inventoryPicked) {
		return { label: getPickListStatusLabel(row), tone: 'success' };
	}
	if (!isCompleted && isRowIncompletePick(row)) {
		return { label: getPickListStatusLabel(row), tone: 'warning' };
	}

	return { label: getPickListStatusLabel(row), tone: 'neutral' };
};

const getAssignment = ({ row, isOwned, isCompleted, isPrintingLabel }) => {
	if (isCompleted) {
		return {
			tag: 'Tub sticker',
			action: isOwned ? 'Tap to view or reprint' : 'Assigned to another picker',
		};
	}
	if (!isOwned) {
		return { tag: row.pickerName || 'Unassigned', action: 'View only' };
	}
	if (row.labelOnly) {
		return { tag: 'Your label', action: isPrintingLabel ? 'Queueing label…' : 'Tap to send label' };
	}

	return { tag: 'Your pick', action: 'Tap to open' };
};

/**
 * One machine of a route on Picking My Day: route order, site and position,
 * machine number, pick status, packing labels and who picks it. Only the
 * picker's own rows open (pick list, label print or the completed pick's tub
 * sticker); coworkers' rows are view only.
 * Props: row (usePickerMyDay row), routeOrder, isOwned, isCompleted,
 * isPrintingLabel, onOpen(row)
 */
const PickingWorkRow = ({ row, routeOrder, isOwned, isCompleted = false, isPrintingLabel = false, onOpen }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const status = getStatus(row, isCompleted);
	const assignment = getAssignment({ row, isOwned, isCompleted, isPrintingLabel });
	const isPressable = isOwned && !isPrintingLabel;
	const isQuiet = isCompleted || !isOwned;
	const title = row.locationName || row.placeName || row.machineName || 'No location details';
	const position = row.locationName ? row.placeName : null;
	const packTags = isCompleted ? [] : row.packTags;
	const handlePress = useCallback(() => onOpen(row), [onOpen, row]);

	return (
		<Pressable
			onPress={isPressable ? handlePress : undefined}
			disabled={!isPressable}
			accessibilityRole={isOwned ? 'button' : undefined}
			accessibilityLabel={[
				`${routeOrder}.`,
				title,
				position,
				row.assetId && `machine ${row.assetId}`,
				status.label,
				packTags.length && `Packing label: ${packTags.map(tag => tag.name).join(', ')}`,
				assignment.tag,
				assignment.action,
			].filter(Boolean).join(', ')}
			accessibilityState={{ disabled: !isPressable, busy: isPrintingLabel }}
			style={({ pressed }) => [styles.row, isQuiet && styles.rowQuiet, pressed && styles.pressed]}
		>
			{packTags.length > 0 && (
				<View style={styles.packing}>
					<Text style={styles.packingTitle}>Packing label</Text>
					<View style={styles.packingTags}>
						{packTags.map(tag => (
							<View key={tag.id} style={styles.packingTag}>
								<Text style={styles.packingTagText}>{tag.name}</Text>
							</View>
						))}
					</View>
				</View>
			)}
			<View style={styles.main}>
				<View style={[styles.order, isOwned && !isCompleted && styles.orderOwned]}>
					<Text style={[styles.orderText, isOwned && !isCompleted && styles.orderTextOwned]}>{routeOrder}</Text>
				</View>
				<Text style={[styles.title, isQuiet && styles.titleQuiet]}>{title}</Text>
				<StatusBadge label={status.label} tone={status.tone} size="small" />
			</View>
			{Boolean(row.assetId || position) && (
				<View style={[styles.indented, styles.metaRow]}>
					<MachineChip number={row.assetId} muted={isQuiet} />
					{Boolean(position) && <Text style={styles.position}>{position}</Text>}
				</View>
			)}
			<View style={[styles.indented, styles.footer]}>
				<Tag label={assignment.tag} tone={isOwned && !isCompleted ? 'primary' : 'neutral'} />
				<Text style={styles.action} numberOfLines={1}>{assignment.action}</Text>
				{isPressable && <Feather name="chevron-right" size={20} color={colors.iconMuted} />}
			</View>
		</Pressable>
	);
};

const createStyles = ({ ui: { colors, radius, type, space } }) => ({
	row: {
		gap: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	rowQuiet: {
		borderColor: colors.card,
		backgroundColor: colors.card,
	},
	pressed: {
		opacity: 0.85,
	},
	packing: {
		gap: 6,
		paddingHorizontal: space.md,
		paddingVertical: space.sm,
		borderRadius: radius.control,
		borderWidth: 1,
		borderColor: colors.warningBorder,
		backgroundColor: colors.warningBg,
	},
	packingTitle: {
		...type.overline,
		color: colors.warning,
	},
	packingTags: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 6,
	},
	packingTag: {
		maxWidth: '100%',
		borderRadius: radius.tag,
		paddingHorizontal: space.sm,
		paddingVertical: space.xs,
		backgroundColor: colors.warningTagBg,
	},
	packingTagText: {
		...type.rowTitleSemibold,
		color: colors.warning,
	},
	main: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.md,
	},
	order: {
		width: ORDER_SIZE,
		height: ORDER_SIZE,
		borderRadius: radius.pill,
		borderWidth: 2,
		borderColor: colors.iconMuted,
		alignItems: 'center',
		justifyContent: 'center',
	},
	orderOwned: {
		borderColor: colors.accent,
	},
	orderText: {
		...type.label,
		color: colors.textSecondary,
	},
	orderTextOwned: {
		color: colors.accent,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		flex: 1,
		paddingTop: space.xs,
	},
	titleQuiet: {
		color: colors.textSecondary,
	},
	// Lines under the title start where the title starts (after the order circle).
	indented: {
		paddingLeft: ORDER_SIZE + space.md,
	},
	metaRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
	},
	position: {
		...type.secondary,
		color: colors.textSecondary,
		flexShrink: 1,
	},
	footer: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.sm,
		marginTop: space.xs,
	},
	action: {
		...type.secondaryStrong,
		color: colors.textSecondary,
		flex: 1,
		textAlign: 'right',
	},
});

export default React.memo(PickingWorkRow);
